// Adaptadores de NAVEGADOR para a EventQueue (D.5.3/D.5.4). Port fiel do transporte do tracker.ts v1
// (POST /events, sendBeacon no sync, fetch keepalive no async, headers X-SYNQ-TOKEN), agora atrás da
// interface injetável da fila. Trocar de runtime = trocar este arquivo, não a fila.

/** Transporte browser: envia um lote a POST /events. Retorna sucesso (bool). */
export function browserTransport({ baseUrl, tenantId, sdkToken, publicKey }) {
  // A CREDENCIAL VAI NO HEADER QUE O PORTÃO LÊ.
  //
  // O identity source do HTTP API é `X-SYNQ-TOKEN`. Faltando ele, o API Gateway devolve 401 SEM
  // sequer chamar o authorizer — sem log, sem tenant, sem como o cliente descobrir por quê.
  //
  // Até 29/09/2026 a chave pública era mandada em `X-SYNQI3-PUBLIC-KEY` + `X-SYNQI3-TENANT`. Grep
  // nos `apps/` e nas `stacks/`: ZERO leitores dos dois. Medido no ar contra api.synqi3.ai/events:
  //
  //     só X-SYNQI3-PUBLIC-KEY ....... HTTP 401
  //     sem credencial nenhuma ....... HTTP 401
  //
  // Idêntico. A opção `publicKey` era porta desenhada e nunca aberta, e um cliente ficou dois meses
  // sem emitir por causa dela.
  //
  // Ela não precisa de porta nova. A chave pública (`pk_`) é gravada por hash na tabela de tokens,
  // exatamente como a `sk_`, e o authorizer já a resolve por esse caminho. O que a restringe não é
  // o segredo — é `allowed_origins`, conferido contra a `Origin` de cada requisição no ingest
  // (`apps/ingest/app.py`, `_common/origem.py`), mais o escopo de navegador que ela declara.
  const credencial = sdkToken || publicKey;
  const headers = { "Content-Type": "application/json" };
  if (credencial) headers["X-SYNQ-TOKEN"] = credencial;
  const url = `${String(baseUrl).replace(/\/$/, "")}/events`;
  return async (batch, opts = {}) => {
    const body = JSON.stringify({ tenant_id: tenantId, events: batch });
    // POR QUE O UNLOAD NÃO USA `sendBeacon`.
    //
    // `navigator.sendBeacon(url, blob)` não carrega header nenhum — a API não tem onde pôr um. O
    // lote de saída de página ia SEM credencial, tomava 401, e o `sendBeacon` devolvia `true`, que
    // é "o navegador aceitou enfileirar", nunca "o servidor recebeu". A fila descartava o evento
    // por tê-lo dado como entregue.
    //
    // `fetch(..., {keepalive: true})` sobrevive ao unload E aceita header. O mesmo caminho nos dois
    // casos deixa UM transporte, em vez de dois que divergem calados — que foi o defeito.
    //
    // `opts.sync` continua chegando aqui (o `onExit` drena com ele) e continua não mudando nada: o
    // que o unload exigia era o `keepalive`, e ele já está ligado sempre.
    try {
      const r = await fetch(url, { method: "POST", headers, body, keepalive: true });
      if (r.ok) return true;
      // 4xx é recusa do PEDIDO: credencial, origem, escopo, ou corpo acima do teto do portão.
      // Repetir não muda a resposta, e repetir calado foi o que escondeu um 403 por quatro meses
      // e meio. 5xx e falha de rede continuam temporários, e esses a fila persiste para replay.
      return { ok: false, permanente: r.status >= 400 && r.status < 500, status: r.status };
    } catch {
      return false;                                  // rede caiu: temporário, vale reenviar
    }
  };
}

/**
 * O `localStorage` DESTA página, ou `null` — e `null` é resposta, não exceção.
 *
 * Este SDK roda na página do CLIENTE, e ali o armazenamento é opcional de três maneiras: janela
 * privada, dado de site bloqueado, e ambiente que expõe um objeto PARCIAL. Qualquer acesso pode
 * levantar, e um `throw` aqui não perde um evento: ele quebra a inicialização do SDK na página de
 * quem nos contratou.
 *
 * Achado em 27/10/2026, quando o Node da máquina passou de 22 para 25 e começou a expor um
 * `localStorage` nativo SEM `removeItem`: `TypeError: localStorage.removeItem is not a function`,
 * subindo de `new Tracker(...)`. O ambiente mudou e revelou o defeito; o defeito era do SDK.
 *
 * A verificação é por MÉTODO, não por presença do objeto: foi exatamente a presença que enganou.
 */
function _loja() {
  try {
    const l = typeof localStorage !== "undefined" ? localStorage : null;
    if (!l || typeof l.getItem !== "function" || typeof l.setItem !== "function"
        || typeof l.removeItem !== "function") return null;
    return l;
  } catch {
    return null;                       // o próprio acesso levanta quando o dado de site é bloqueado
  }
}

/** Persistência browser sobre localStorage (D.5.4). Sem armazenamento, não guarda e não quebra. */
export function browserStorage(key = "synqi3_pending") {
  return {
    read() {
      const l = _loja();
      if (!l) return [];               // sem armazenamento: nada guardado, que é verdade
      try {
        const raw = l.getItem(key);
        return raw ? JSON.parse(raw) : [];
      } catch {
        return [];
      }
    },
    write(list) {
      const l = _loja();
      if (!l) return;                  // nada a fazer, e NUNCA um throw na página do cliente
      try {
        if (list && list.length) l.setItem(key, JSON.stringify(list));
        else l.removeItem(key);
      } catch {
        // Cota cheia ou escrita bloqueada. O evento segue na fila em memória: perder a
        // persistência é degradar, perder a página do cliente é outra coisa.
      }
    },
  };
}

/**
 * Persistência browser de UM VALOR (a decisão guardada), e não de uma lista.
 *
 * É função separada do `browserStorage` porque os contratos de ausência são diferentes, e um flag
 * escondendo a diferença faria a distinção sumir na hora errada:
 *
 *     browserStorage ....... lista; vazia É ausência, e `read()` sem nada devolve `[]`
 *     browserValueStorage .. valor; ausência é `null`, e `[]` seria um valor legítimo
 *
 * `write(null)` APAGA — é assim que um opt-out limpa a decisão guardada, e é por isso que o
 * `decisao.js` devolve `null` em vez de `{}` quando não há o que guardar.
 */
export function browserValueStorage(key) {
  return {
    read() {
      const l = _loja();
      if (!l) return null;             // sem armazenamento: nenhuma decisão guardada
      let raw = null;
      try {
        raw = l.getItem(key);
      } catch {
        return null;
      }
      if (raw == null) return null;
      try {
        return JSON.parse(raw);
      } catch {
        // Guardado ilegível é guardado que alguém (ou outra versão) corrompeu. Apagar é a resposta:
        // deixá-lo ali faria toda visita futura pagar o mesmo parse e falhar igual.
        try { l.removeItem(key); } catch { /* bloqueado: segue sem apagar */ }
        return null;
      }
    },
    write(v) {
      const l = _loja();
      if (!l) return;
      try {
        if (v == null) l.removeItem(key);
        else l.setItem(key, JSON.stringify(v));
      } catch {
        // CONSENTIMENTO QUE NÃO PERSISTE é o caso que mais dói aqui: a decisão vale para esta
        // visita e será perguntada de novo na próxima. Pior que isso seria quebrar a página.
      }
    },
  };
}

/**
 * Registra os handlers de saída (D.5.3): visibilitychange→hidden e pagehide drenam em modo sync.
 * NÃO usa `beforeunload` nem `unload` (ignorados no mobile e invalidam o bfcache).
 */
export function registerExitHooks(queue) {
  if (typeof document !== "undefined" && document.addEventListener) {
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") queue.onExit();
    });
  }
  if (typeof window !== "undefined" && window.addEventListener) {
    window.addEventListener("pagehide", () => queue.onExit());
  }
}
